/**
 * OnboardingScreen
 * Single-screen wizard: country, variant, and lesson language.
 * Shown once to all users who haven't completed onboarding.
 *
 * Country-first approach: selecting a country auto-derives the Spanish variant.
 * Uses LanguageRegionPicker for the shared controls.
 */

import React, { useState, useCallback } from 'react';
import { ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { VStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_MD, FS_LG, FS_2XL } from '../theme';
import { ErrorModal } from '../components/ui/ErrorModal';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import {
  selectCompleteOnboarding,
  selectPreferencesIsLoading,
  selectPreferencesError,
  selectClearPreferencesError,
} from '../store/userPreferencesSelectors';
import { detectCountry, suggestSourceLocaleFromOSLanguage } from '../types/locale-detection';
import { getCountryConfig } from '../data/countryConfig';
import { LanguageRegionPicker } from '../components/LanguageRegionPicker';
import type { LessonLanguage } from '../types/locale';
import { useCourseStore, findCourseForLocale } from '../store/courseStore';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

// ---------------------------------------------------------------------------
// Display component (presentational — no store dependency)
// ---------------------------------------------------------------------------

export interface OnboardingScreenDisplayProps {
  /** ISO country code detected from OS locale, or undefined */
  detectedCountry: string | undefined;
  /** Initial country selection (defaults to detectedCountry if in list, or none) */
  initialCountry?: string;
  /** Initial variant selection */
  initialVariant?: string;
  /** Initial target locale */
  initialTargetLocale?: string;
  /** Initial lesson language */
  initialLessonLanguage?: LessonLanguage;
  /** Whether the confirm action is in progress */
  isLoading: boolean;
  /** Error message to display in modal (e.g. API failure) */
  error?: string | null;
  /** Called to dismiss the error */
  onClearError?: () => void;
  /** Called with (country, variant, targetLocale, lessonLanguage) when user taps Confirm */
  onConfirm: (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage) => void;
}

export function OnboardingScreenDisplay({
  detectedCountry,
  initialCountry,
  initialVariant,
  initialTargetLocale,
  initialLessonLanguage = 'source',
  isLoading,
  error,
  onClearError,
  onConfirm,
}: OnboardingScreenDisplayProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const countries = useCourseStore((s) => s.countries);

  // Resolve initial country: explicit prop > detected (if in list) > none
  const resolvedInitial = initialCountry ?? (detectedCountry && getCountryConfig(detectedCountry, countries) ? detectedCountry : undefined);
  const startConfig = resolvedInitial ? getCountryConfig(resolvedInitial, countries) : undefined;

  // Local state — batched until Continue
  const [selectedCountry, setSelectedCountry] = useState<string | null>(resolvedInitial ?? null);
  // Initial variant: explicit prop > detected country's default > OS-language
  // suggestion > none. When the OS gives us nothing to go on, '' preselects
  // nothing — the user picks a country and that drives the variant
  // (LOCALE-004-02). Never fabricate a preselection from the course list.
  const initialSource = initialVariant ?? (startConfig?.locales.length ? startConfig.locales[0] : undefined) ?? suggestSourceLocaleFromOSLanguage() ?? '';
  const [selectedVariant, setSelectedVariant] = useState<string>(initialSource);
  const [selectedTargetLocale, setSelectedTargetLocale] = useState<string>(
    initialTargetLocale ?? findCourseForLocale(initialSource)?.defaultTargetLocale ?? '',
  );
  const [selectedLessonLanguage, setSelectedLessonLanguage] = useState<LessonLanguage>(initialLessonLanguage);

  const handleCountrySelect = useCallback((code: string) => {
    setSelectedCountry(code);
    const config = getCountryConfig(code, countries);
    if (config && config.locales.length > 0) {
      const newSourceLocale = config.locales[0];
      setSelectedVariant(newSourceLocale);

      // If the new locale belongs to a different course, update target locale too
      const match = findCourseForLocale(newSourceLocale);
      if (match) {
        setSelectedTargetLocale(match.defaultTargetLocale);
      }
    }
  }, [countries]);

  const handleVariantSelect = useCallback((variant: string) => {
    setSelectedVariant(variant);
  }, []);

  const handleTargetLocaleChange = useCallback((locale: string) => {
    setSelectedTargetLocale(locale);
  }, []);

  const handleLessonLanguageChange = useCallback((lang: LessonLanguage) => {
    setSelectedLessonLanguage(lang);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedCountry && selectedVariant) {
      onConfirm(selectedCountry, selectedVariant, selectedTargetLocale, selectedLessonLanguage);
    }
  }, [onConfirm, selectedCountry, selectedVariant, selectedTargetLocale, selectedLessonLanguage]);

  // Picking a country always sets a variant (handleCountrySelect), so the
  // variant guard only matters for the no-preselection start state.
  const canConfirm = selectedCountry !== null && selectedVariant !== '' && !isLoading;

  return (
    <>
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.background }}
        contentContainerStyle={{ padding: 24, paddingTop: 60 }}
      >
        <VStack space="xl">
          {/* Header */}
          <VStack space="sm">
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_2XL * fontScale,
                fontWeight: '700',
                color: theme.text,
                textAlign: 'center',
                ...ffs,
              }}
            >
              {t('onboarding.title')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_MD * fontScale,
                color: theme.textSecondary,
                textAlign: 'center',
                ...ffs,
              }}
            >
              {t('onboarding.subtitle')}
            </Text>
          </VStack>

          {/* Language & Region Picker */}
          <LanguageRegionPicker
            country={selectedCountry}
            sourceLocale={selectedVariant}
            targetLocale={selectedTargetLocale}
            lessonLanguage={selectedLessonLanguage}
            detectedCountry={detectedCountry}
            isLoading={isLoading}
            onCountrySelect={handleCountrySelect}
            onVariantSelect={handleVariantSelect}
            onTargetLocaleChange={handleTargetLocaleChange}
            onLessonLanguageChange={handleLessonLanguageChange}
          />

          {/* Confirm button */}
          <TouchableOpacity
            testID="onboarding-confirm"
            accessibilityLabel={t('onboarding.confirm')}
            accessibilityRole="button"
            onPress={handleConfirm}
            disabled={!canConfirm}
            activeOpacity={0.7}
            style={{
              backgroundColor: colors.primary,
              borderRadius: 12,
              paddingVertical: 16,
              alignItems: 'center',
              opacity: canConfirm ? 1 : 0.4,
              marginTop: 8,
              marginBottom: 40,
            }}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_LG * fontScale,
                  fontWeight: '700',
                  color: '#fff',
                  ...ffs,
                }}
              >
                {t('onboarding.confirm')}
              </Text>
            )}
          </TouchableOpacity>
        </VStack>
      </ScrollView>

      <ErrorModal
        visible={!!error}
        message={error ?? ''}
        onDismiss={onClearError ?? (() => {})}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Connected screen (reads from Zustand store + OS locale)
// ---------------------------------------------------------------------------

export function OnboardingScreen() {
  const completeOnboarding = useUserPreferencesStore(selectCompleteOnboarding);
  const isLoading = useUserPreferencesStore(selectPreferencesIsLoading);
  const prefsError = useUserPreferencesStore(selectPreferencesError);
  const clearPrefsError = useUserPreferencesStore(selectClearPreferencesError);
  const detectedCountry = detectCountry();

  const handleConfirm = useCallback(
    async (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage) => {
      await completeOnboarding(country, variant, targetLocale, lessonLanguage);
    },
    [completeOnboarding],
  );

  return (
    <OnboardingScreenDisplay
      detectedCountry={detectedCountry}
      isLoading={isLoading}
      error={prefsError}
      onClearError={clearPrefsError}
      onConfirm={handleConfirm}
    />
  );
}
